import { AnimatePresence, motion } from "framer-motion";
import { styled, Box, Input, Tooltip, Button } from "prism-react";
import * as React from "react";

import { useClickEvents } from "hooks/useClickEvents";
import { slugifyBranchName } from "utils/git";

import { useTitleState } from "./useTitleState";

const AnimatedDiv = styled(motion.div);
const AnimatedBox = motion(Box);

type RenamableTitleProps = {
  onRename: (value: string) => Promise<unknown>;
  value: string;
  displayValue: string;
  canRename: boolean;
  onClick: (e: Event | React.SyntheticEvent) => void;
  projectType: "branch" | "sandbox";
};

export const RenamableTitle: React.FC<RenamableTitleProps> = ({
  onRename,
  value,
  displayValue,
  canRename,
  onClick,
  projectType,
}) => {
  const {
    inputRef,
    containerRef,
    setShowRenameForm,
    showRenameForm,
    containerTranslation,
    mutatedValue,
    setMutatedValue,
  } = useTitleState();

  React.useEffect(() => {
    if (!displayValue) {
      return;
    }
    setMutatedValue(displayValue);
  }, [displayValue, setMutatedValue]);

  const hybridClick = useClickEvents({
    click: onClick,
    doubleClick: () => setShowRenameForm(true),
  });

  const slugifiedNewBranchName = slugifyBranchName(mutatedValue);
  const noPermissionsTooltip =
    projectType === "branch" ? "Change branch" : "See details";

  return (
    <AnimatedDiv
      ref={containerRef}
      animate={showRenameForm ? "translated" : "resting"}
      css={{
        position: "relative",
      }}
      variants={{
        translated: {
          x: containerTranslation,
          transition: {
            delay: 0.1,
            type: "spring",
            bounce: 0,
          },
        },
        resting: {
          x: 0,
        },
      }}
    >
      {/* 
        The two boxes below are always mounted but kept exclusively hidden depending
        on the state of the component. This is done to prevent the input jumping around too much when 
        unmounting and remounting while animating.
      */}
      <Box
        aria-hidden={!showRenameForm}
        as="form"
        css={{
          textAlign: "center",
          position: showRenameForm ? "relative" : "absolute",
          display: showRenameForm ? "block" : "none",
        }}
        onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          onRename(mutatedValue).finally(() => {
            setShowRenameForm(false);
          });
        }}
      >
        <Input
          ref={inputRef}
          autoComplete="off"
          css={{
            padding: 0,
            background: "transparent",
            textAlign: "center",

            "&:focus": {
              outline: "none",
            },
          }}
          maxLength={80}
          name="branchName"
          onBlur={() => setShowRenameForm(false)}
          onChange={(e) => {
            setMutatedValue(e.target.value);
          }}
          onKeyDown={(event) =>
            event.code === "Escape" && setShowRenameForm(false)
          }
          // arbitrary increase of length to accomodate for non-monospaced font
          size={mutatedValue.length + 1}
          value={mutatedValue}
          required
        />

        {slugifiedNewBranchName !== mutatedValue.trim() ? (
          <AnimatePresence>
            <AnimatedBox
              animate={{ opacity: 1, y: 0 }}
              css={{
                position: "absolute",
                inset: 0,
                top: "100%",
                opacity: 0.8,
                fontSize: 11,
              }}
              exit={{ opacity: 0, y: 4 }}
              initial={{ opacity: 0, y: 4 }}
            >
              {slugifiedNewBranchName}
            </AnimatedBox>
          </AnimatePresence>
        ) : null}
      </Box>

      <Box
        aria-hidden={showRenameForm}
        css={
          showRenameForm
            ? {
                display: "none",
                opacity: 0,
                pointerEvents: "none",
                position: "absolute",
              }
            : { position: "relative", display: "block" }
        }
      >
        <Tooltip
          content={canRename ? "Double click to rename" : noPermissionsTooltip}
        >
          <Button
            aria-label="Rename branch"
            css={{
              padding: "$1",
              color: "$neutral-fg-subtle",
            }}
            onClick={canRename ? hybridClick : onClick}
          >
            {value}
          </Button>
        </Tooltip>
      </Box>
    </AnimatedDiv>
  );
};
